import { Container } from '@almbrand/container';
import { Grid } from '@almbrand/grid';
import { RichText } from '@almbrand/richtext';
import { Tag } from '@almbrand/tag';
import { HeadlineComponent } from 'components';
import { ButtonComponent } from 'components/1-atoms/ButtonComponent/ButtonComponent';
import { PageContext } from 'context/PageContext';
import { memo, useContext, useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'store/index';
import { selectBasketItems, updateItemQuantity } from 'store/slices/basketSlice';
import {
	getBasketItemUrl,
	getNextBasketItem,
	loadBasketJourney,
	startBasketJourney,
} from '../../../services/BasketJourneyService';
import { formatAmount, parseDanishAmount } from '../../../utilities/format';
import styles from './ProductCalculatorSelectorTemplate.module.scss';

const getProductId = (
	product: NonNullable<ProductCalculatorSelectorTemplateModel['products']>[number],
	index: number
): string => {
	return product.salesProduct ?? product.url ?? `${index}`;
};

export interface ProductCalculatorSelectorTemplateProps {
	pageModel: ProductCalculatorSelectorTemplateModel;
}

export const ProductCalculatorSelectorTemplate: React.FC<ProductCalculatorSelectorTemplateProps> = memo(
	({ pageModel }) => {
		const { theme } = useContext(PageContext);
		const title = pageModel?.title;
		const description = pageModel?.description;
		const products = pageModel?.products ?? [];
		const basketItems = useAppSelector(selectBasketItems);
		const dispatch = useAppDispatch();
		const [navigationError, setNavigationError] = useState<string>();
		const [isStarting, setIsStarting] = useState(false);
		const journey = loadBasketJourney();
		const matchesBasket =
			journey?.items.length === basketItems.length &&
			journey.items.every(
				(item, index) =>
					item.basketItemId === basketItems[index].basketItemId && item.url === basketItems[index].url
			);
		const isComplete = matchesBasket && journey.items.length > 0 && !getNextBasketItem(journey);
		const canContinue = basketItems.length > 0 && basketItems.every((item) => Boolean(item.url));
		const monthlyTotal = isComplete
			? journey.items.reduce(
					(total, item) =>
						total +
						parseDanishAmount(
							journey.results[item.basketItemId].calculatorForm.SELECTED_PACKAGE_MONTHLY_PRICE?.data
								?.value
						),
					0
				)
			: 0;

		const continueBasket = () => {
			if (!canContinue || isStarting) return;
			try {
				const nextJourney =
					matchesBasket && journey.activeBasketItemId
						? journey
						: startBasketJourney(basketItems, location.href);
				const nextItem = getNextBasketItem(nextJourney);
				if (nextItem) {
					setIsStarting(true);
					window.location.assign(getBasketItemUrl(nextItem));
				}
			} catch (error) {
				console.error('Failed to open basket calculation', error);
				setIsStarting(false);
				setNavigationError('Vi kunne ikke åbne beregningen. Prøv igen.');
			}
		};

		//TODO: Remove when pushing to main
		useEffect(() => {
			console.log('Selected product quantities updated: ', basketItems);
		}, [basketItems]);

		const getQuantityFromTagValue = (productId: string, value: string | undefined): number => {
			if (!value) {
				return 0;
			}

			const parsedQuantity = Number.parseInt(value, 10);
			if (!Number.isNaN(parsedQuantity)) {
				return Math.max(parsedQuantity, 0);
			}

			return value === productId ? 1 : 0;
		};

		const onTagValueChange = (
			product: NonNullable<ProductCalculatorSelectorTemplateModel['products']>[number],
			productId: string,
			value: string | undefined
		): void => {
			const quantity = getQuantityFromTagValue(productId, value);

			dispatch(
				updateItemQuantity({
					item: {
						id: productId,
						name: product.title ?? '',
						url: product.url ?? '',
					},
					quantity,
				})
			);
		};

		return (
			<div
				data-page-type='ProductCalculatorSelectorTemplate'
				className={styles.ProductCalculatorSelectorTemplate}
			>
				<Container
					mobileWidth='fullWidth'
					noPadding
				>
					<Grid nogutter>
						<div className={styles.ProductCalculatorSelectorTemplate__headline}>
							<HeadlineComponent
								title={title}
								heading='h3'
								size='default'
								fontColor='primary'
							/>

							{description && <RichText description={description} />}
						</div>
					</Grid>
				</Container>

				{products.length > 0 && (
					<ul
						data-testid='product-calculator-selector-products'
						className={styles.ProductCalculatorSelectorTemplate__productList}
					>
						{products.map((product, index) => {
							const productId = getProductId(product, index);

							const options =
								product.maxInBasket !== undefined && product.maxInBasket > 1
									? Array.from({ length: product.maxInBasket + 1 }, (_, index) => {
											const value = index.toString();

											return {
												value,
												label: value,
											};
										})
									: undefined;

							return (
								<li key={productId}>
									<Tag
										iconProps={{
											themeName: theme,
											icon: product.icon?.icon, //getProductIcon(product.imageUrl) as never,
										}}
										checkboxValue={productId}
										optionValue={basketItems
											.filter((item) => item.id === productId)
											.length.toString()}
										title={product.title}
										onValueChange={(value) => {
											onTagValueChange(product, productId, value);
										}}
										checked={basketItems.some((item) => item.id === productId)}
										{...(options ? { options } : {})}
										optionsCount
									/>
								</li>
							);
						})}
					</ul>
				)}
				{isComplete ? (
					<section className={styles.ProductCalculatorSelectorTemplate__summary}>
						<HeadlineComponent
							title='Dine forsikringer'
							heading='h3'
							size='default'
							fontColor='primary'
						/>
						<dl>
							{journey.items.map((item) => {
								const result = journey.results[item.basketItemId];
								const price = parseDanishAmount(
									result.calculatorForm.SELECTED_PACKAGE_MONTHLY_PRICE?.data?.value
								);
								return (
									<div key={item.basketItemId}>
										<dt>
											{item.name} ({result.selectedPackage})
										</dt>
										<dd>{formatAmount(price)} kr./md.</dd>
									</div>
								);
							})}
							<div>
								<dt>I alt</dt>
								<dd>{formatAmount(monthlyTotal)} kr./md.</dd>
							</div>
						</dl>
					</section>
				) : (
					<ButtonComponent
						type='button'
						buttonType='primary'
						label='Næste'
						isValid={canContinue && !isStarting}
						isLoading={isStarting}
						onClick={continueBasket}
					>
						{''}
					</ButtonComponent>
				)}
				{navigationError && <p role='alert'>{navigationError}</p>}
			</div>
		);
	}
);

ProductCalculatorSelectorTemplate.displayName = 'ProductCalculatorSelectorTemplate';
